import { Box, CircularProgress } from '@mui/material';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import React, { useLayoutEffect, useRef, useState } from 'react';
import AppCard from '../../components/AppCard';
import AppCardSkeleton from '../../components/atoms/AppCardSkeleton';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import type { Product } from '../../redux/slices/productList';
import { homeGridRowSx, useHomeGridLayout } from './homeGridLayout';
import { ItemWrapper, ProductGrid, ScrollableContainer } from './Home.styles';

type HomeProductGridProps = {
    loading: boolean;
    items: Product[];
    hasMore: boolean;
    isLoadingMore: boolean;
    onLoadMore: () => void;
};

const HomeProductGrid = React.memo(function HomeProductGrid({
    loading,
    items,
    hasMore,
    isLoadingMore,
    onLoadMore
}: HomeProductGridProps) {
    const layout = useHomeGridLayout();
    const gridRef = useRef<HTMLDivElement>(null);
    const [scrollMargin, setScrollMargin] = useState(0);

    const sentinelRef = useInfiniteScroll({
        onLoadMore,
        hasMore,
        isLoading: loading,
        isLoadingMore
    });

    useLayoutEffect(() => {
        const measure = () => {
            if (gridRef.current) {
                setScrollMargin(gridRef.current.offsetTop);
            }
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [loading, items.length, layout.columnCount]);

    const rowCount = Math.ceil(items.length / layout.columnCount);

    const rowVirtualizer = useWindowVirtualizer({
        count: rowCount,
        estimateSize: () => layout.rowStride,
        overscan: 2,
        scrollMargin
    });

    if (loading) {
        return (
            <ScrollableContainer>
                <ProductGrid>
                    {Array.from({ length: layout.columnCount * 2 }, (_, i) => (
                        <ItemWrapper key={`skel-${i}`}>
                            <AppCardSkeleton />
                        </ItemWrapper>
                    ))}
                </ProductGrid>
            </ScrollableContainer>
        );
    }

    if (!items.length) {
        return null;
    }

    const virtualRows = rowVirtualizer.getVirtualItems();

    return (
        <ScrollableContainer ref={gridRef}>
            <Box
                sx={{
                    height: rowVirtualizer.getTotalSize(),
                    width: '100%',
                    position: 'relative'
                }}
            >
                {virtualRows.map((virtualRow) => {
                    const startIndex = virtualRow.index * layout.columnCount;
                    const rowItems = items.slice(startIndex, startIndex + layout.columnCount);
                    const isLastRow = virtualRow.index === rowCount - 1;

                    return (
                        <Box
                            key={virtualRow.key}
                            data-index={virtualRow.index}
                            ref={rowVirtualizer.measureElement}
                            sx={{
                                ...homeGridRowSx(layout),
                                ...(isLastRow ? { paddingBottom: 0 } : {}),
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                transform: `translateY(${virtualRow.start - scrollMargin}px)`
                            }}
                        >
                            {rowItems.map((item, colIndex) => {
                                const index = startIndex + colIndex;
                                return (
                                    <ItemWrapper key={item.id ?? `item-${index}`}>
                                        <AppCard type="normal" details={item} />
                                    </ItemWrapper>
                                );
                            })}
                        </Box>
                    );
                })}
            </Box>

            {hasMore && (
                <Box ref={sentinelRef} sx={{ width: '100%', minHeight: 4, flexShrink: 0 }} aria-hidden />
            )}

            {isLoadingMore && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        py: 3,
                        minHeight: 72
                    }}
                    aria-live="polite"
                    aria-busy="true"
                >
                    <CircularProgress size={32} thickness={4} />
                </Box>
            )}

            {!hasMore && items.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, minHeight: 24 }} aria-hidden />
            )}
        </ScrollableContainer>
    );
});

export default HomeProductGrid;
